import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { Keyboard, KeyboardStyle } from '@capacitor/keyboard'

// The one call that actually makes the WebView draw full-screen (behind the
// status bar). On iOS this used to be a single fire-and-forget call whose
// failure was silently swallowed - if the native bridge wasn't fully ready
// the instant index.tsx ran, or iOS reset the setting after the app was
// backgrounded, the app would render with a visible top gap and nobody would
// ever see why. Retries a few times, and logs (rather than swallows) if every
// attempt fails so it's at least visible in device logs.
async function applyFullScreenOverlay(attempt = 0): Promise<void> {
  try {
    await StatusBar.setOverlaysWebView({ overlay: true })
  } catch (err) {
    if (attempt < 3) {
      setTimeout(() => applyFullScreenOverlay(attempt + 1), 300 * (attempt + 1))
    } else {
      console.error('[nativeStatusBar] setOverlaysWebView failed after retries - app may not be full-screen', err)
    }
  }
}

// Full-screen (edge-to-edge): let the WebView draw BEHIND the status bar so the
// app fills the whole screen with no reserved top margin. The clock/battery stay
// visible because content is kept below them via safe-area-inset-top padding (the
// body padding + each screen's header). Light icons suit our dark UI. No-op on web.
export function initStatusBar() {
  if (!Capacitor.isNativePlatform()) return
  applyFullScreenOverlay()
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {}) // light icons for our dark UI

  // iOS keyboard is translucent and composites against the WebView background,
  // which is gold (#F5A623, set for the launch splash) - so the keys picked up a
  // gold tint. Forcing the DARK keyboard makes it opaque and on-brand, so nothing
  // bleeds through regardless of what's behind it or how the list is scrolled.
  // No-op on Android (opaque keyboard already). Requires a native build.
  Keyboard.setStyle({ style: KeyboardStyle.Dark }).catch(() => {})
  // Remove the grey prev/next/Done accessory bar above the keyboard (the floating
  // ▲▼✓ pill), matching Bolt/Uber. iOS-only.
  Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {})

  // Re-apply on every foreground - some iOS versions/WKWebView configurations
  // reset the overlay-webview flag when the app was backgrounded, which is
  // exactly the kind of "sometimes not full-screen" flakiness this is meant
  // to close out entirely, not just fix on cold start.
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) applyFullScreenOverlay()
    })
  }).catch(() => {})
}

// Most of the app is dark, so the status bar defaults to light (white) icons.
// A handful of screens - like the white "Enter Destination" search overlay -
// invert that, so call this with `true` while one of those is on screen and
// `false` when it closes. No-op on web.
export function setStatusBarLightBackground(isLightBackground: boolean) {
  if (!Capacitor.isNativePlatform()) return
  StatusBar.setStyle({ style: isLightBackground ? Style.Light : Style.Dark }).catch(() => {})
}
